import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIntegrationStatus } from "@/hooks/use-integration-status";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { 
  Search, 
  Package, 
  Clock, 
  CheckCircle,
  AlertTriangle,
  Download,
  Filter,
  RefreshCw,
  ExternalLink,
  Truck,
  FileText,
  Calendar,
  DollarSign,
  MapPin,
  User,
  Phone,
  Mail,
  Building,
  ClipboardList,
  ArrowRight,
  Printer,
  Plus,
  Zap,
  Settings2,
  X
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { AdvancedFiltersModal } from "@/components/advanced-filters-modal";
import { FilterRow, FilterLogic, ActiveFilterChip } from "@/types/filters";
import { applyFilters, generateFilterChips } from "@/lib/filter-utils";
import { ConfigureColumnsModal, ColumnDefinition, ColumnConfig } from "@/components/configure-columns-modal";
import {
  exportToCSV,
  exportToExcel,
  getCurrentDateForFilename,
} from "@/lib/export-utils";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";

// Types
interface PurchaseOrderLine {
  id: string;
  lineNumber: number;
  stockItemCode: string;
  stockItemName: string;
  description: string;
  quantityOrdered: number;
  quantityReceived: number;
  quantityOutstanding: number;
  unitPrice: number;
  lineTotal: number;
  expectedDeliveryDate: Date | null;
  projectCode?: string;
  projectName?: string;
}

interface PurchaseOrder {
  id: string;
  poNumber: string;
  supplierCode: string;
  supplierName: string;
  supplierContact: string;
  supplierEmail: string;
  supplierPhone: string;
  orderDate: Date;
  expectedDeliveryDate: Date;
  status: "open" | "partially-received" | "fully-received" | "cancelled" | "overdue";
  totalValue: number;
  currency: string;
  deliveryAddress: {
    line1: string;
    line2?: string;
    city: string;
    postcode: string;
    country: string;
  };
  paymentTerms: string;
  notes: string;
  lines: PurchaseOrderLine[];
  projectCode?: string;
  projectName?: string;
  syncedFrom: string;
  lastSyncDate: Date;
  createdBy: string;
  approvedBy: string;
  receivingHistory: ReceivingRecord[];
}

interface ReceivingRecord {
  id: string;
  date: Date;
  receivedBy: string;
  reference: string;
  lines: {
    lineNumber: number;
    stockItemCode: string;
    quantityReceived: number;
  }[];
}

// Mock data
const MOCK_PURCHASE_ORDERS: PurchaseOrder[] = [
  {
    id: "1",
    poNumber: "PO-2024-001",
    supplierCode: "SUP-001",
    supplierName: "Supplier A Ltd",
    supplierContact: "John Smith",
    supplierEmail: "john@suppliera.com",
    supplierPhone: "+44 20 1234 5678",
    orderDate: new Date("2024-01-15"),
    expectedDeliveryDate: new Date("2024-02-15"),
    status: "partially-received",
    totalValue: 15750.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Please deliver to loading bay 3. Contact warehouse manager on arrival.",
    projectCode: "PRJ-001",
    projectName: "Website Redesign 2024",
    lines: [
      {
        id: "l1",
        lineNumber: 1,
        stockItemCode: "WGT-A",
        stockItemName: "Widget A",
        description: "Premium quality widget - Model A",
        quantityOrdered: 150,
        quantityReceived: 100,
        quantityOutstanding: 50,
        unitPrice: 45.00,
        lineTotal: 6750.00,
        expectedDeliveryDate: new Date("2024-02-15"),
        projectCode: "PRJ-001",
        projectName: "Website Redesign 2024"
      },
      {
        id: "l2",
        lineNumber: 2,
        stockItemCode: "PART-C",
        stockItemName: "Component C",
        description: "Standard component C - Pack of 10",
        quantityOrdered: 200,
        quantityReceived: 200,
        quantityOutstanding: 0,
        unitPrice: 22.50,
        lineTotal: 4500.00,
        expectedDeliveryDate: new Date("2024-02-10"),
        projectCode: "PRJ-001",
        projectName: "Website Redesign 2024"
      },
      {
        id: "l3",
        lineNumber: 3,
        stockItemCode: "RAW-X",
        stockItemName: "Raw Material X",
        description: "Raw material X - 25kg bags",
        quantityOrdered: 100,
        quantityReceived: 0,
        quantityOutstanding: 100,
        unitPrice: 45.00,
        lineTotal: 4500.00,
        expectedDeliveryDate: new Date("2024-02-20"),
        projectCode: "PRJ-001",
        projectName: "Website Redesign 2024"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-20T10:30:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r1",
        date: new Date("2024-01-18"),
        receivedBy: "John Doe",
        reference: "GRN-2024-001",
        lines: [
          { lineNumber: 1, stockItemCode: "WGT-A", quantityReceived: 100 },
          { lineNumber: 2, stockItemCode: "PART-C", quantityReceived: 200 }
        ]
      }
    ]
  },
  {
    id: "2",
    poNumber: "PO-2024-002",
    supplierCode: "SUP-002",
    supplierName: "Supplier B Corp",
    supplierContact: "Emma Davis",
    supplierEmail: "emma@supplierb.com",
    supplierPhone: "+44 20 9876 5432",
    orderDate: new Date("2024-01-20"),
    expectedDeliveryDate: new Date("2024-01-25"),
    status: "overdue",
    totalValue: 8500.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Urgent order - please expedite delivery",
    projectCode: "PRJ-002",
    projectName: "Factory Expansion Phase 2",
    lines: [
      {
        id: "l4",
        lineNumber: 1,
        stockItemCode: "WGT-B",
        stockItemName: "Widget B",
        description: "Widget B - Standard model",
        quantityOrdered: 100,
        quantityReceived: 0,
        quantityOutstanding: 100,
        unitPrice: 85.00,
        lineTotal: 8500.00,
        expectedDeliveryDate: new Date("2024-01-25"),
        projectCode: "PRJ-002",
        projectName: "Factory Expansion Phase 2"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-20T14:15:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: []
  },
  {
    id: "3",
    poNumber: "PO-2024-003",
    supplierCode: "SUP-003",
    supplierName: "Global Supplies Co",
    supplierContact: "David Chen",
    supplierEmail: "david@globalsupplies.com",
    supplierPhone: "+44 20 5555 1234",
    orderDate: new Date("2024-01-22"),
    expectedDeliveryDate: new Date("2024-02-28"),
    status: "open",
    totalValue: 25600.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 45 days",
    notes: "Large order - may require multiple deliveries",
    lines: [
      {
        id: "l5",
        lineNumber: 1,
        stockItemCode: "GDG-B",
        stockItemName: "Gadget B",
        description: "Gadget B - Professional series",
        quantityOrdered: 200,
        quantityReceived: 0,
        quantityOutstanding: 200,
        unitPrice: 128.00,
        lineTotal: 25600.00,
        expectedDeliveryDate: new Date("2024-02-28")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-22T09:00:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: []
  },
  {
    id: "4",
    poNumber: "PO-2024-004",
    supplierCode: "SUP-001",
    supplierName: "Supplier A Ltd",
    supplierContact: "John Smith",
    supplierEmail: "john@suppliera.com",
    supplierPhone: "+44 20 1234 5678",
    orderDate: new Date("2024-01-10"),
    expectedDeliveryDate: new Date("2024-01-20"),
    status: "fully-received",
    totalValue: 5400.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Standard delivery",
    lines: [
      {
        id: "l6",
        lineNumber: 1,
        stockItemCode: "PART-D",
        stockItemName: "Component D",
        description: "Component D - Economy pack",
        quantityOrdered: 300,
        quantityReceived: 300,
        quantityOutstanding: 0,
        unitPrice: 18.00,
        lineTotal: 5400.00,
        expectedDeliveryDate: new Date("2024-01-20")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-19T16:45:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r2",
        date: new Date("2024-01-19"),
        receivedBy: "Jane Smith",
        reference: "GRN-2024-002",
        lines: [
          { lineNumber: 1, stockItemCode: "PART-D", quantityReceived: 300 }
        ]
      }
    ]
  },
  {
    id: "5",
    poNumber: "PO-2024-005",
    supplierCode: "SUP-004",
    supplierName: "Food Supplier Ltd",
    supplierContact: "Maria Garcia",
    supplierEmail: "maria@foodsupplier.com",
    supplierPhone: "+44 20 7777 8888",
    orderDate: new Date("2024-01-25"),
    expectedDeliveryDate: new Date("2024-02-05"),
    status: "open",
    totalValue: 12300.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "456 Cold Storage Facility",
      city: "Manchester",
      postcode: "M1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 14 days",
    notes: "Temperature controlled delivery required. Must arrive before 10am.",
    projectCode: "CC-001",
    projectName: "Marketing Department",
    lines: [
      {
        id: "l7",
        lineNumber: 1,
        stockItemCode: "FOOD-A",
        stockItemName: "Food Product A",
        description: "Fresh food product A - Refrigerated",
        quantityOrdered: 150,
        quantityReceived: 0,
        quantityOutstanding: 150,
        unitPrice: 82.00,
        lineTotal: 12300.00,
        expectedDeliveryDate: new Date("2024-02-05"),
        projectCode: "CC-001",
        projectName: "Marketing Department"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T11:20:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: []
  },
  {
    id: "6",
    poNumber: "PO-2023-999",
    supplierCode: "SUP-002",
    supplierName: "Supplier B Corp",
    supplierContact: "Emma Davis",
    supplierEmail: "emma@supplierb.com",
    supplierPhone: "+44 20 9876 5432",
    orderDate: new Date("2023-12-15"),
    expectedDeliveryDate: new Date("2024-01-05"),
    status: "cancelled",
    totalValue: 3200.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Cancelled due to supplier stock issues",
    lines: [
      {
        id: "l8",
        lineNumber: 1,
        stockItemCode: "PART-E",
        stockItemName: "Component E",
        description: "Component E - Special order",
        quantityOrdered: 80,
        quantityReceived: 0,
        quantityOutstanding: 0,
        unitPrice: 40.00,
        lineTotal: 3200.00,
        expectedDeliveryDate: new Date("2024-01-05")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-08T13:30:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: []
  },
  // Transaction History Reference Orders
  {
    id: "7",
    poNumber: "PO-2024-050",
    supplierCode: "SUP-003",
    supplierName: "Global Supplies Ltd",
    supplierContact: "Robert Chen",
    supplierEmail: "robert@globalsupplies.com",
    supplierPhone: "+44 20 5555 1234",
    orderDate: new Date("2024-01-05"),
    expectedDeliveryDate: new Date("2024-01-12"),
    status: "received",
    totalValue: 12500.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Initial stock purchase for Premium Wireless Headphones",
    lines: [
      {
        id: "l9",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Premium Wireless Headphones",
        description: "Premium Wireless Headphones - Black",
        quantityOrdered: 500,
        quantityReceived: 500,
        quantityOutstanding: 0,
        unitPrice: 25.00,
        lineTotal: 12500.00,
        expectedDeliveryDate: new Date("2024-01-12")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-05T09:00:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r2",
        date: new Date("2024-01-12"),
        receivedBy: "John Doe",
        reference: "GRN-2024-050",
        lines: [
          { lineNumber: 1, stockItemCode: "STK001", quantityReceived: 500 }
        ]
      }
    ]
  },
  {
    id: "8",
    poNumber: "PO-2024-058",
    supplierCode: "SUP-003",
    supplierName: "Global Supplies Ltd",
    supplierContact: "Robert Chen",
    supplierEmail: "robert@globalsupplies.com",
    supplierPhone: "+44 20 5555 1234",
    orderDate: new Date("2024-01-18"),
    expectedDeliveryDate: new Date("2024-01-25"),
    status: "received",
    totalValue: 7500.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Restock order for Premium Wireless Headphones",
    lines: [
      {
        id: "l10",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Premium Wireless Headphones",
        description: "Premium Wireless Headphones - Black",
        quantityOrdered: 300,
        quantityReceived: 300,
        quantityOutstanding: 0,
        unitPrice: 25.00,
        lineTotal: 7500.00,
        expectedDeliveryDate: new Date("2024-01-25")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-18T10:15:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r3",
        date: new Date("2024-01-25"),
        receivedBy: "John Doe",
        reference: "GRN-2024-058",
        lines: [
          { lineNumber: 1, stockItemCode: "STK001", quantityReceived: 300 }
        ]
      }
    ]
  },
  {
    id: "9",
    poNumber: "PO-2024-065",
    supplierCode: "SUP-003",
    supplierName: "Global Supplies Ltd",
    supplierContact: "Robert Chen",
    supplierEmail: "robert@globalsupplies.com",
    supplierPhone: "+44 20 5555 1234",
    orderDate: new Date("2024-02-01"),
    expectedDeliveryDate: new Date("2024-02-08"),
    status: "received",
    totalValue: 10000.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Bulk order for Premium Wireless Headphones",
    lines: [
      {
        id: "l11",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Premium Wireless Headphones",
        description: "Premium Wireless Headphones - Black",
        quantityOrdered: 400,
        quantityReceived: 400,
        quantityOutstanding: 0,
        unitPrice: 25.00,
        lineTotal: 10000.00,
        expectedDeliveryDate: new Date("2024-02-08")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-02-01T11:30:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r4",
        date: new Date("2024-02-08"),
        receivedBy: "John Doe",
        reference: "GRN-2024-065",
        lines: [
          { lineNumber: 1, stockItemCode: "STK001", quantityReceived: 400 }
        ]
      }
    ]
  },
  {
    id: "10",
    poNumber: "PO-2024-072",
    supplierCode: "SUP-003",
    supplierName: "Global Supplies Ltd",
    supplierContact: "Robert Chen",
    supplierEmail: "robert@globalsupplies.com",
    supplierPhone: "+44 20 5555 1234",
    orderDate: new Date("2024-02-15"),
    expectedDeliveryDate: new Date("2024-02-22"),
    status: "received",
    totalValue: 6250.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Regular restock for Premium Wireless Headphones",
    lines: [
      {
        id: "l12",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Premium Wireless Headphones",
        description: "Premium Wireless Headphones - Black",
        quantityOrdered: 250,
        quantityReceived: 250,
        quantityOutstanding: 0,
        unitPrice: 25.00,
        lineTotal: 6250.00,
        expectedDeliveryDate: new Date("2024-02-22")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-02-15T09:45:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r5",
        date: new Date("2024-02-22"),
        receivedBy: "John Doe",
        reference: "GRN-2024-072",
        lines: [
          { lineNumber: 1, stockItemCode: "STK001", quantityReceived: 250 }
        ]
      }
    ]
  },
  {
    id: "11",
    poNumber: "PO-2024-080",
    supplierCode: "SUP-004",
    supplierName: "Tech Distributors Inc",
    supplierContact: "Lisa Wang",
    supplierEmail: "lisa@techdist.com",
    supplierPhone: "+44 20 7777 8888",
    orderDate: new Date("2024-03-01"),
    expectedDeliveryDate: new Date("2024-03-08"),
    status: "received",
    totalValue: 4500.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "456 Distribution Center",
      line2: "Tech Park",
      city: "Manchester",
      postcode: "M1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Order for Ergonomic Office Chair",
    lines: [
      {
        id: "l13",
        lineNumber: 1,
        stockItemCode: "STK003",
        stockItemName: "Ergonomic Office Chair",
        description: "Ergonomic Office Chair - Black Mesh",
        quantityOrdered: 30,
        quantityReceived: 30,
        quantityOutstanding: 0,
        unitPrice: 150.00,
        lineTotal: 4500.00,
        expectedDeliveryDate: new Date("2024-03-08")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-03-01T10:00:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r6",
        date: new Date("2024-03-08"),
        receivedBy: "Jane Smith",
        reference: "GRN-2024-080",
        lines: [
          { lineNumber: 1, stockItemCode: "STK003", quantityReceived: 30 }
        ]
      }
    ]
  },
  {
    id: "12",
    poNumber: "PO-2024-085",
    supplierCode: "SUP-003",
    supplierName: "Global Supplies Ltd",
    supplierContact: "Robert Chen",
    supplierEmail: "robert@globalsupplies.com",
    supplierPhone: "+44 20 5555 1234",
    orderDate: new Date("2024-03-05"),
    expectedDeliveryDate: new Date("2024-03-12"),
    status: "received",
    totalValue: 8750.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "March restock for Premium Wireless Headphones",
    lines: [
      {
        id: "l14",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Premium Wireless Headphones",
        description: "Premium Wireless Headphones - Black",
        quantityOrdered: 350,
        quantityReceived: 350,
        quantityOutstanding: 0,
        unitPrice: 25.00,
        lineTotal: 8750.00,
        expectedDeliveryDate: new Date("2024-03-12")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-03-05T11:20:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r7",
        date: new Date("2024-03-12"),
        receivedBy: "John Doe",
        reference: "GRN-2024-085",
        lines: [
          { lineNumber: 1, stockItemCode: "STK001", quantityReceived: 350 }
        ]
      }
    ]
  },
  {
    id: "13",
    poNumber: "PO-2024-095",
    supplierCode: "SUP-005",
    supplierName: "Office Supplies Co",
    supplierContact: "David Brown",
    supplierEmail: "david@officesupplies.com",
    supplierPhone: "+44 20 3333 4444",
    orderDate: new Date("2024-03-18"),
    expectedDeliveryDate: new Date("2024-03-25"),
    status: "received",
    totalValue: 2400.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "789 Office Park",
      line2: "Business District",
      city: "Birmingham",
      postcode: "B1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Order for Stainless Steel Water Bottle",
    lines: [
      {
        id: "l15",
        lineNumber: 1,
        stockItemCode: "STK002",
        stockItemName: "Stainless Steel Water Bottle",
        description: "Stainless Steel Water Bottle - 750ml",
        quantityOrdered: 120,
        quantityReceived: 120,
        quantityOutstanding: 0,
        unitPrice: 20.00,
        lineTotal: 2400.00,
        expectedDeliveryDate: new Date("2024-03-25")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-03-18T14:30:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r8",
        date: new Date("2024-03-25"),
        receivedBy: "Jane Smith",
        reference: "GRN-2024-095",
        lines: [
          { lineNumber: 1, stockItemCode: "STK002", quantityReceived: 120 }
        ]
      }
    ]
  },
  {
    id: "14",
    poNumber: "PO-2024-098",
    supplierCode: "SUP-003",
    supplierName: "Global Supplies Ltd",
    supplierContact: "Robert Chen",
    supplierEmail: "robert@globalsupplies.com",
    supplierPhone: "+44 20 5555 1234",
    orderDate: new Date("2024-03-22"),
    expectedDeliveryDate: new Date("2024-03-29"),
    status: "received",
    totalValue: 5000.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "End of March restock for Premium Wireless Headphones",
    lines: [
      {
        id: "l16",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Premium Wireless Headphones",
        description: "Premium Wireless Headphones - Black",
        quantityOrdered: 200,
        quantityReceived: 200,
        quantityOutstanding: 0,
        unitPrice: 25.00,
        lineTotal: 5000.00,
        expectedDeliveryDate: new Date("2024-03-29")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-03-22T09:15:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r9",
        date: new Date("2024-03-29"),
        receivedBy: "John Doe",
        reference: "GRN-2024-098",
        lines: [
          { lineNumber: 1, stockItemCode: "STK001", quantityReceived: 200 }
        ]
      }
    ]
  },
  {
    id: "15",
    poNumber: "PO-2024-100",
    supplierCode: "SUP-005",
    supplierName: "Office Supplies Co",
    supplierContact: "David Brown",
    supplierEmail: "david@officesupplies.com",
    supplierPhone: "+44 20 3333 4444",
    orderDate: new Date("2024-04-02"),
    expectedDeliveryDate: new Date("2024-04-09"),
    status: "received",
    totalValue: 1600.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "789 Office Park",
      line2: "Business District",
      city: "Birmingham",
      postcode: "B1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "April restock for Stainless Steel Water Bottle",
    lines: [
      {
        id: "l17",
        lineNumber: 1,
        stockItemCode: "STK002",
        stockItemName: "Stainless Steel Water Bottle",
        description: "Stainless Steel Water Bottle - 750ml",
        quantityOrdered: 80,
        quantityReceived: 80,
        quantityOutstanding: 0,
        unitPrice: 20.00,
        lineTotal: 1600.00,
        expectedDeliveryDate: new Date("2024-04-09")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-04-02T10:45:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r10",
        date: new Date("2024-04-09"),
        receivedBy: "Jane Smith",
        reference: "GRN-2024-100",
        lines: [
          { lineNumber: 1, stockItemCode: "STK002", quantityReceived: 80 }
        ]
      }
    ]
  },
  {
    id: "16",
    poNumber: "PO-2024-115",
    supplierCode: "SUP-003",
    supplierName: "Global Supplies Ltd",
    supplierContact: "Robert Chen",
    supplierEmail: "robert@globalsupplies.com",
    supplierPhone: "+44 20 5555 1234",
    orderDate: new Date("2024-04-15"),
    expectedDeliveryDate: new Date("2024-04-22"),
    status: "received",
    totalValue: 6250.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "Mid-April restock for Premium Wireless Headphones",
    lines: [
      {
        id: "l18",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Premium Wireless Headphones",
        description: "Premium Wireless Headphones - Black",
        quantityOrdered: 250,
        quantityReceived: 250,
        quantityOutstanding: 0,
        unitPrice: 25.00,
        lineTotal: 6250.00,
        expectedDeliveryDate: new Date("2024-04-22")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-04-15T11:00:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r11",
        date: new Date("2024-04-22"),
        receivedBy: "John Doe",
        reference: "GRN-2024-115",
        lines: [
          { lineNumber: 1, stockItemCode: "STK001", quantityReceived: 250 }
        ]
      }
    ]
  },
  {
    id: "17",
    poNumber: "PO-2024-120",
    supplierCode: "SUP-004",
    supplierName: "Tech Distributors Inc",
    supplierContact: "Lisa Wang",
    supplierEmail: "lisa@techdist.com",
    supplierPhone: "+44 20 7777 8888",
    orderDate: new Date("2024-04-25"),
    expectedDeliveryDate: new Date("2024-05-02"),
    status: "received",
    totalValue: 3000.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "456 Distribution Center",
      line2: "Tech Park",
      city: "Manchester",
      postcode: "M1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "April restock for Ergonomic Office Chair",
    lines: [
      {
        id: "l19",
        lineNumber: 1,
        stockItemCode: "STK003",
        stockItemName: "Ergonomic Office Chair",
        description: "Ergonomic Office Chair - Black Mesh",
        quantityOrdered: 20,
        quantityReceived: 20,
        quantityOutstanding: 0,
        unitPrice: 150.00,
        lineTotal: 3000.00,
        expectedDeliveryDate: new Date("2024-05-02")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-04-25T13:30:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r12",
        date: new Date("2024-05-02"),
        receivedBy: "Jane Smith",
        reference: "GRN-2024-120",
        lines: [
          { lineNumber: 1, stockItemCode: "STK003", quantityReceived: 20 }
        ]
      }
    ]
  },
  {
    id: "18",
    poNumber: "PO-2024-123",
    supplierCode: "SUP-003",
    supplierName: "Global Supplies Ltd",
    supplierContact: "Robert Chen",
    supplierEmail: "robert@globalsupplies.com",
    supplierPhone: "+44 20 5555 1234",
    orderDate: new Date("2024-05-01"),
    expectedDeliveryDate: new Date("2024-05-08"),
    status: "received",
    totalValue: 7500.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "123 Warehouse Street",
      line2: "Industrial Estate",
      city: "London",
      postcode: "SW1A 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "May restock for Premium Wireless Headphones",
    lines: [
      {
        id: "l20",
        lineNumber: 1,
        stockItemCode: "STK001",
        stockItemName: "Premium Wireless Headphones",
        description: "Premium Wireless Headphones - Black",
        quantityOrdered: 300,
        quantityReceived: 300,
        quantityOutstanding: 0,
        unitPrice: 25.00,
        lineTotal: 7500.00,
        expectedDeliveryDate: new Date("2024-05-08")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-05-01T09:30:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r13",
        date: new Date("2024-05-08"),
        receivedBy: "John Doe",
        reference: "GRN-2024-123",
        lines: [
          { lineNumber: 1, stockItemCode: "STK001", quantityReceived: 300 }
        ]
      }
    ]
  },
  {
    id: "19",
    poNumber: "PO-2024-125",
    supplierCode: "SUP-005",
    supplierName: "Office Supplies Co",
    supplierContact: "David Brown",
    supplierEmail: "david@officesupplies.com",
    supplierPhone: "+44 20 3333 4444",
    orderDate: new Date("2024-05-05"),
    expectedDeliveryDate: new Date("2024-05-12"),
    status: "received",
    totalValue: 2000.00,
    currency: "GBP",
    deliveryAddress: {
      line1: "789 Office Park",
      line2: "Business District",
      city: "Birmingham",
      postcode: "B1 1AA",
      country: "United Kingdom"
    },
    paymentTerms: "Net 30 days",
    notes: "May restock for Stainless Steel Water Bottle",
    lines: [
      {
        id: "l21",
        lineNumber: 1,
        stockItemCode: "STK002",
        stockItemName: "Stainless Steel Water Bottle",
        description: "Stainless Steel Water Bottle - 750ml",
        quantityOrdered: 100,
        quantityReceived: 100,
        quantityOutstanding: 0,
        unitPrice: 20.00,
        lineTotal: 2000.00,
        expectedDeliveryDate: new Date("2024-05-12")
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-05-05T10:15:00"),
    createdBy: "Sarah Johnson",
    approvedBy: "Mike Wilson",
    receivingHistory: [
      {
        id: "r14",
        date: new Date("2024-05-12"),
        receivedBy: "Jane Smith",
        reference: "GRN-2024-125",
        lines: [
          { lineNumber: 1, stockItemCode: "STK002", quantityReceived: 100 }
        ]
      }
    ]
  }
];

// All available columns with descriptions
const ALL_PO_COLUMNS: ColumnDefinition[] = [
  { id: "poNumber", name: "PO Number", description: "Purchase order number" },
  { id: "supplier", name: "Supplier", description: "Supplier name and code" },
  { id: "project", name: "Project/Cost Center", description: "Associated project or cost center" },
  { id: "orderDate", name: "Order Date", description: "Date the PO was created" },
  { id: "expectedDelivery", name: "Expected Delivery", description: "Expected delivery date" },
  { id: "status", name: "Status", description: "Current PO status" },
  { id: "totalValue", name: "Total Value", description: "Total order value" },
  { id: "lineCount", name: "Items", description: "Number of line items" },
  { id: "fulfillment", name: "Fulfillment", description: "Percentage of items received" },
  { id: "createdBy", name: "Created By", description: "User who created the PO" },
  { id: "approvedBy", name: "Approved By", description: "User who approved the PO" },
  { id: "paymentTerms", name: "Payment Terms", description: "Payment terms" },
  { id: "currency", name: "Currency", description: "Order currency" },
  { id: "lastSync", name: "Last Sync", description: "Last sync date from ERP" },
  { id: "actions", name: "Actions", description: "Available actions" },
];

// Default visible columns
const DEFAULT_VISIBLE_PO_COLUMNS = [
  "poNumber",
  "supplier",
  "project",
  "orderDate",
  "expectedDelivery",
  "status",
  "totalValue",
  "lineCount",
  "fulfillment",
  "actions",
];

export function PurchaseOrdersPage() {
  const { navigateTo, pageParams } = useNavigation();
  const integrationStatus = useIntegrationStatus();
  
  // Help system
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("purchase-orders");
  
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(pageParams?.statusFilter || "all");
  const [supplierFilter, setSupplierFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showQuickAddDrawer, setShowQuickAddDrawer] = useState(false);
  
  // Advanced filters state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterRows, setFilterRows] = useState<FilterRow[]>([]);
  const [filterLogic, setFilterLogic] = useState<FilterLogic[]>([]);
  const [activeFilterChips, setActiveFilterChips] = useState<ActiveFilterChip[]>([]);
  
  // Configure columns state
  const [showConfigureColumns, setShowConfigureColumns] = useState(false);
  const [columnConfig, setColumnConfig] = useState<ColumnConfig>({
    visibleColumns: DEFAULT_VISIBLE_PO_COLUMNS,
    columnOrder: ALL_PO_COLUMNS.map(col => col.id),
  });

  // Handle navigation from transaction history
  useEffect(() => {
    if (pageParams?.transactionId) {
      const poNumber = pageParams.transactionId as string;
      const po = MOCK_PURCHASE_ORDERS.find(p => p.poNumber === poNumber);
      if (po) {
        setSelectedPO(po);
        setShowDetailDrawer(true);
      }
    }
  }, [pageParams]);

  // Calculate summary statistics
  const stats = useMemo(() => {
    const openPOs = MOCK_PURCHASE_ORDERS.filter(po => po.status === "open").length;
    const awaitingDelivery = MOCK_PURCHASE_ORDERS.filter(po => 
      po.status === "open" || po.status === "partially-received"
    ).length;
    const partiallyReceived = MOCK_PURCHASE_ORDERS.filter(po => po.status === "partially-received").length;
    const overdue = MOCK_PURCHASE_ORDERS.filter(po => po.status === "overdue").length;

    return { openPOs, awaitingDelivery, partiallyReceived, overdue };
  }, []);

  // Get unique suppliers for filter
  const suppliers = useMemo(() => {
    const uniqueSuppliers = Array.from(new Set(MOCK_PURCHASE_ORDERS.map(po => po.supplierName)));
    return uniqueSuppliers.sort();
  }, []);

  // Get unique projects/cost centers for filter
  const projects = useMemo(() => {
    const uniqueProjects = Array.from(
      new Set(
        MOCK_PURCHASE_ORDERS
          .filter(po => po.projectCode && po.projectName)
          .map(po => JSON.stringify({ code: po.projectCode, name: po.projectName }))
      )
    ).map(str => JSON.parse(str));
    return uniqueProjects.sort((a, b) => a.code.localeCompare(b.code));
  }, []);

  // Filter data
  const filteredData = useMemo(() => {
    let filtered = MOCK_PURCHASE_ORDERS;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(po =>
        po.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.supplierCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (po.projectCode && po.projectCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (po.projectName && po.projectName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      if (statusFilter === "outstanding") {
        // Outstanding includes: open, partially-received, and overdue
        filtered = filtered.filter(po => 
          po.status === "open" || 
          po.status === "partially-received" || 
          po.status === "overdue"
        );
      } else {
        filtered = filtered.filter(po => po.status === statusFilter);
      }
    }

    // Filter by supplier
    if (supplierFilter !== "all") {
      filtered = filtered.filter(po => po.supplierName === supplierFilter);
    }

    // Filter by project/cost center
    if (projectFilter !== "all") {
      filtered = filtered.filter(po => po.projectCode === projectFilter);
    }

    // Apply advanced filters
    if (filterRows.length > 0) {
      // Transform PO data to include computed fields for filtering
      const transformedData = filtered.map(po => ({
        ...po,
        lineCount: po.lines.length,
        fulfillmentPercentage: getPOFulfillmentPercentage(po),
        isOverdue: isOverdue(po),
        hasOutstanding: po.lines.some(line => line.quantityOutstanding > 0),
      }));
      
      filtered = applyFilters(transformedData, filterRows, filterLogic);
    }

    return filtered;
  }, [searchTerm, statusFilter, supplierFilter, projectFilter, filterRows, filterLogic]);

  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  // Format date and time
  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = "GBP") => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "open":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Open</Badge>;
      case "partially-received":
        return <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 border-yellow-200">Partially Received</Badge>;
      case "fully-received":
        return <Badge variant="success" accent="success">Fully Received</Badge>;
      case "overdue":
        return <Badge variant="destructive">Overdue</Badge>;
      case "cancelled":
        return <Badge variant="secondary">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Check if PO is overdue
  const isOverdue = (po: PurchaseOrder) => {
    return po.status !== "fully-received" && 
           po.status !== "cancelled" && 
           new Date() > po.expectedDeliveryDate;
  };

  // Get days until/overdue
  const getDaysUntilDelivery = (date: Date) => {
    const days = Math.floor((date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Handle row click
  const handleRowClick = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setShowDetailDrawer(true);
  };

  // Handle receive goods
  const handleReceiveGoods = (po: PurchaseOrder) => {
    navigateTo("receive-goods", { 
      poId: po.id,
      poNumber: po.poNumber,
      returnTo: "purchase-orders"
    });
  };

  // Handle sync
  const handleSync = () => {
    toast.promise(
      new Promise((resolve) => setTimeout(resolve, 1500)),
      {
        loading: "Syncing with Access Financials...",
        success: "Purchase orders synced successfully",
        error: "Failed to sync purchase orders",
      }
    );
  };

  // Calculate line fulfillment percentage
  const getLineFulfillmentPercentage = (line: PurchaseOrderLine) => {
    return (line.quantityReceived / line.quantityOrdered) * 100;
  };

  // Calculate overall PO fulfillment
  const getPOFulfillmentPercentage = (po: PurchaseOrder) => {
    const totalOrdered = po.lines.reduce((sum, line) => sum + line.quantityOrdered, 0);
    const totalReceived = po.lines.reduce((sum, line) => sum + line.quantityReceived, 0);
    return totalOrdered > 0 ? (totalReceived / totalOrdered) * 100 : 0;
  };

  // Handle advanced filters
  const handleApplyAdvancedFilters = (rows: FilterRow[], logic: FilterLogic[]) => {
    setFilterRows(rows);
    setFilterLogic(logic);
    setActiveFilterChips(generateFilterChips(rows, logic));
    setShowAdvancedFilters(false);
    toast.success(`Applied ${rows.length} filter${rows.length !== 1 ? 's' : ''}`);
  };

  const handleRemoveFilterChip = (chipId: string) => {
    const updatedRows = filterRows.filter(row => row.id !== chipId);
    const updatedLogic = filterLogic.slice(0, updatedRows.length - 1);
    setFilterRows(updatedRows);
    setFilterLogic(updatedLogic);
    setActiveFilterChips(generateFilterChips(updatedRows, updatedLogic));
    
    if (updatedRows.length === 0) {
      toast.info("All filters cleared");
    }
  };

  const handleClearAllFilters = () => {
    setFilterRows([]);
    setFilterLogic([]);
    setActiveFilterChips([]);
    toast.info("All filters cleared");
  };

  // Handle column configuration
  const handleSaveColumnConfig = (config: ColumnConfig) => {
    setColumnConfig(config);
    setShowConfigureColumns(false);
    toast.success("Column configuration saved");
  };

  // Handle export
  const handleExport = (format: 'csv' | 'excel') => {
    const dataToExport = filteredData.map(po => ({
      'PO Number': po.poNumber,
      'Supplier Code': po.supplierCode,
      'Supplier Name': po.supplierName,
      'Project Code': po.projectCode || '',
      'Project Name': po.projectName || '',
      'Order Date': formatDate(po.orderDate),
      'Expected Delivery': formatDate(po.expectedDeliveryDate),
      'Status': po.status,
      'Total Value': po.totalValue,
      'Currency': po.currency,
      'Line Count': po.lines.length,
      'Fulfillment %': Math.round(getPOFulfillmentPercentage(po)),
      'Created By': po.createdBy,
      'Approved By': po.approvedBy,
      'Payment Terms': po.paymentTerms,
      'Last Sync': formatDateTime(po.lastSyncDate),
    }));

    const filename = `purchase-orders-${getCurrentDateForFilename()}`;
    const columns = Object.keys(dataToExport[0] || {});
    
    if (format === 'csv') {
      exportToCSV(dataToExport, columns, `${filename}.csv`);
    } else {
      exportToExcel(dataToExport, columns, `${filename}.xlsx`);
    }
  };

  // Check if column is visible
  const isColumnVisible = (columnId: string) => {
    return columnConfig.visibleColumns.includes(columnId);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Purchase Orders</h1>
              {integrationStatus.isFinancialsIntegrated && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Synced from Access Financials
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-2">
              View and track purchase orders{integrationStatus.isFinancialsIntegrated ? " synced from your ERP system" : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <HelpButton onClick={toggleHelp} showLabel />
            {/* <Button onClick={() => setShowQuickAddDrawer(true)} variant="default">
              <Plus className="h-4 w-4 mr-2" />
              Quick Add Purchase Order
            </Button> */}
            <Button onClick={handleSync} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Now
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StaticMetricCardAlt
          title="Open POs"
          value={stats.openPOs.toString()}
          icon={Package}
          variant="info"
          changeLabel="Awaiting delivery"
        />
        <StaticMetricCardAlt
          title="Awaiting Delivery"
          value={stats.awaitingDelivery.toString()}
          icon={Truck}
          variant="info"
          changeLabel="Open or partially received"
        />
        <StaticMetricCardAlt
          title="Partially Received"
          value={stats.partiallyReceived.toString()}
          icon={Clock}
          variant="warning"
          changeLabel="Awaiting remaining items"
        />
        <StaticMetricCardAlt
          title="Overdue"
          value={stats.overdue.toString()}
          icon={AlertTriangle}
          variant="error"
          changeLabel="Past expected delivery date"
        />
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Purchase Orders</CardTitle>
              <CardDescription>
                View purchase orders synced from Access Financials
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowAdvancedFilters(true)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Advanced Filters
                {activeFilterChips.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {activeFilterChips.length}
                  </Badge>
                )}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowConfigureColumns(true)}
              >
                <Settings2 className="h-4 w-4 mr-2" />
                Columns
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleExport('excel')}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by PO number or supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="outstanding">Outstanding</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="partially-received">Partially Received</SelectItem>
                <SelectItem value="fully-received">Fully Received</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Supplier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier} value={supplier}>
                    {supplier}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="Project/Cost Center" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects/Cost Centers</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.code} value={project.code}>
                    {project.code} - {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Active Filter Chips */}
          {activeFilterChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {activeFilterChips.map((chip) => (
                <Badge
                  key={chip.id}
                  variant="secondary"
                  className="gap-1 pr-1"
                >
                  {chip.displayText}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-transparent"
                    onClick={() => handleRemoveFilterChip(chip.id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAllFilters}
                className="h-6 text-xs"
              >
                Clear all
              </Button>
            </div>
          )}

          {/* Results count */}
          <div className="text-sm text-muted-foreground mb-4">
            Showing {filteredData.length} of {MOCK_PURCHASE_ORDERS.length} purchase orders
          </div>

          {/* Data Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  {isColumnVisible("poNumber") && <TableHead>PO Number</TableHead>}
                  {isColumnVisible("supplier") && <TableHead>Supplier</TableHead>}
                  {isColumnVisible("project") && <TableHead>Project/Cost Center</TableHead>}
                  {isColumnVisible("orderDate") && <TableHead>Order Date</TableHead>}
                  {isColumnVisible("expectedDelivery") && <TableHead>Expected Delivery</TableHead>}
                  {isColumnVisible("status") && <TableHead>Status</TableHead>}
                  {isColumnVisible("totalValue") && <TableHead className="text-right">Total Value</TableHead>}
                  {isColumnVisible("lineCount") && <TableHead className="text-center">Items</TableHead>}
                  {isColumnVisible("fulfillment") && <TableHead className="text-center">Fulfillment</TableHead>}
                  {isColumnVisible("createdBy") && <TableHead>Created By</TableHead>}
                  {isColumnVisible("approvedBy") && <TableHead>Approved By</TableHead>}
                  {isColumnVisible("paymentTerms") && <TableHead>Payment Terms</TableHead>}
                  {isColumnVisible("currency") && <TableHead>Currency</TableHead>}
                  {isColumnVisible("lastSync") && <TableHead>Last Sync</TableHead>}
                  {isColumnVisible("actions") && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      No purchase orders found matching your criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((po) => {
                    const fulfillment = getPOFulfillmentPercentage(po);
                    const daysUntil = getDaysUntilDelivery(po.expectedDeliveryDate);
                    
                    return (
                      <TableRow 
                        key={po.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(po)}
                      >
                        {isColumnVisible("poNumber") && (
                          <TableCell className="font-medium font-mono">{po.poNumber}</TableCell>
                        )}
                        {isColumnVisible("supplier") && (
                          <TableCell>
                            <div>
                              <div className="font-medium">{po.supplierName}</div>
                              <div className="text-sm text-muted-foreground">{po.supplierCode}</div>
                            </div>
                          </TableCell>
                        )}
                        {isColumnVisible("project") && (
                          <TableCell>
                            {po.projectCode && po.projectName ? (
                              <div>
                                <div className="font-medium text-sm">{po.projectCode}</div>
                                <div className="text-xs text-muted-foreground">{po.projectName}</div>
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        )}
                        {isColumnVisible("orderDate") && (
                          <TableCell className="font-mono text-sm">
                            {formatDate(po.orderDate)}
                          </TableCell>
                        )}
                        {isColumnVisible("expectedDelivery") && (
                          <TableCell>
                            <div className="font-mono text-sm">
                              {formatDate(po.expectedDeliveryDate)}
                            </div>
                            {po.status !== "fully-received" && po.status !== "cancelled" && (
                              <div className="text-xs text-muted-foreground">
                                {daysUntil < 0 ? (
                                  <span className="text-destructive font-medium">
                                    {Math.abs(daysUntil)} days overdue
                                  </span>
                                ) : daysUntil === 0 ? (
                                  <span className="text-yellow-600 font-medium">Due today</span>
                                ) : (
                                  <span>In {daysUntil} days</span>
                                )}
                              </div>
                            )}
                          </TableCell>
                        )}
                        {isColumnVisible("status") && (
                          <TableCell>{getStatusBadge(po.status)}</TableCell>
                        )}
                        {isColumnVisible("totalValue") && (
                          <TableCell className="text-right font-mono font-medium">
                            {formatCurrency(po.totalValue, po.currency)}
                          </TableCell>
                        )}
                        {isColumnVisible("lineCount") && (
                          <TableCell className="text-center">
                            <Badge variant="outline">{po.lines.length}</Badge>
                          </TableCell>
                        )}
                        {isColumnVisible("fulfillment") && (
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 bg-muted rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full ${
                                    fulfillment === 100 ? 'bg-green-600' :
                                    fulfillment > 0 ? 'bg-yellow-600' : 'bg-gray-300'
                                  }`}
                                  style={{ width: `${fulfillment}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium">{Math.round(fulfillment)}%</span>
                            </div>
                          </TableCell>
                        )}
                        {isColumnVisible("createdBy") && (
                          <TableCell className="text-sm">{po.createdBy}</TableCell>
                        )}
                        {isColumnVisible("approvedBy") && (
                          <TableCell className="text-sm">{po.approvedBy}</TableCell>
                        )}
                        {isColumnVisible("paymentTerms") && (
                          <TableCell className="text-sm">{po.paymentTerms}</TableCell>
                        )}
                        {isColumnVisible("currency") && (
                          <TableCell className="text-sm font-mono">{po.currency}</TableCell>
                        )}
                        {isColumnVisible("lastSync") && (
                          <TableCell className="text-sm font-mono">
                            {formatDateTime(po.lastSyncDate)}
                          </TableCell>
                        )}
                        {isColumnVisible("actions") && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {po.status !== "fully-received" && po.status !== "cancelled" && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReceiveGoods(po);
                                  }}
                                >
                                  <Truck className="h-4 w-4 mr-1" />
                                  Receive
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toast.info("Print functionality coming soon");
                                }}
                              >
                                <Printer className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Drawer */}
      <Sheet open={showDetailDrawer} onOpenChange={setShowDetailDrawer}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto p-3">
          {selectedPO && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <SheetTitle className="font-mono">{selectedPO.poNumber}</SheetTitle>
                    {getStatusBadge(selectedPO.status)}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => toast.info("View in Access Financials")}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View in ERP
                  </Button>
                </div>
                <SheetDescription>
                  Purchase order from {selectedPO.supplierName}
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-6 mt-6">
                {/* Sync Information */}
                <div className="bg-muted/50 p-3 rounded-lg text-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Last synced:</span>
                      <span className="font-medium">{formatDateTime(selectedPO.lastSyncDate)}</span>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {selectedPO.syncedFrom}
                    </Badge>
                  </div>
                </div>

                {/* Order Information */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Order Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Order Date</Label>
                      <p className="font-medium font-mono">{formatDate(selectedPO.orderDate)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Expected Delivery</Label>
                      <p className="font-medium font-mono">{formatDate(selectedPO.expectedDeliveryDate)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Created By</Label>
                      <p className="font-medium">{selectedPO.createdBy}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Approved By</Label>
                      <p className="font-medium">{selectedPO.approvedBy}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Payment Terms</Label>
                      <p className="font-medium">{selectedPO.paymentTerms}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Total Value</Label>
                      <p className="font-medium text-lg">{formatCurrency(selectedPO.totalValue, selectedPO.currency)}</p>
                    </div>
                  </div>
                </div>

                {/* Supplier Information */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Supplier Information
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Contact:</span>
                      <span className="font-medium">{selectedPO.supplierContact}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Email:</span>
                      <a href={`mailto:${selectedPO.supplierEmail}`} className="font-medium text-primary hover:underline">
                        {selectedPO.supplierEmail}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Phone:</span>
                      <a href={`tel:${selectedPO.supplierPhone}`} className="font-medium text-primary hover:underline">
                        {selectedPO.supplierPhone}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Delivery Address */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Delivery Address
                  </h3>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p>{selectedPO.deliveryAddress.line1}</p>
                    {selectedPO.deliveryAddress.line2 && <p>{selectedPO.deliveryAddress.line2}</p>}
                    <p>{selectedPO.deliveryAddress.city}</p>
                    <p>{selectedPO.deliveryAddress.postcode}</p>
                    <p>{selectedPO.deliveryAddress.country}</p>
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <ClipboardList className="h-4 w-4" />
                    Line Items
                  </h3>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-right">Ordered</TableHead>
                          <TableHead className="text-right">Received</TableHead>
                          <TableHead className="text-right">Outstanding</TableHead>
                          <TableHead className="text-right">Unit Price</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedPO.lines.map((line) => {
                          const fulfillment = getLineFulfillmentPercentage(line);
                          return (
                            <TableRow key={line.id}>
                              <TableCell className="font-medium">{line.lineNumber}</TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-medium">{line.stockItemName}</div>
                                  <div className="text-sm text-muted-foreground">{line.stockItemCode}</div>
                                  <div className="text-xs text-muted-foreground mt-1">{line.description}</div>
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-mono">{line.quantityOrdered}</TableCell>
                              <TableCell className="text-right">
                                <div className="font-mono">{line.quantityReceived}</div>
                                <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                                  <div 
                                    className={`h-1.5 rounded-full ${
                                      fulfillment === 100 ? 'bg-green-600' :
                                      fulfillment > 0 ? 'bg-yellow-600' : 'bg-gray-300'
                                    }`}
                                    style={{ width: `${fulfillment}%` }}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {line.quantityOutstanding > 0 ? (
                                  <span className="text-yellow-600 font-medium">{line.quantityOutstanding}</span>
                                ) : (
                                  <span className="text-green-600 font-medium">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {formatCurrency(line.unitPrice, selectedPO.currency)}
                              </TableCell>
                              <TableCell className="text-right font-mono font-medium">
                                {formatCurrency(line.lineTotal, selectedPO.currency)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow className="bg-muted/50 font-medium">
                          <TableCell colSpan={6} className="text-right">Total:</TableCell>
                          <TableCell className="text-right font-mono text-lg">
                            {formatCurrency(selectedPO.totalValue, selectedPO.currency)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Receiving History */}
                {selectedPO.receivingHistory.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Receiving History
                    </h3>
                    <div className="space-y-2">
                      {selectedPO.receivingHistory.map((record) => (
                        <div key={record.id} className="border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="font-mono">{record.reference}</Badge>
                              <span className="text-sm text-muted-foreground">by {record.receivedBy}</span>
                            </div>
                            <span className="text-sm text-muted-foreground font-mono">
                              {formatDateTime(record.date)}
                            </span>
                          </div>
                          <div className="text-sm space-y-1">
                            {record.lines.map((line, idx) => (
                              <div key={idx} className="flex justify-between">
                                <span className="text-muted-foreground">Line {line.lineNumber} ({line.stockItemCode}):</span>
                                <span className="font-medium">+{line.quantityReceived} units</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedPO.notes && (
                  <div>
                    <h3 className="font-semibold mb-3">Notes</h3>
                    <p className="text-sm bg-muted p-3 rounded-md">{selectedPO.notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  {selectedPO.status !== "fully-received" && selectedPO.status !== "cancelled" && (
                    <Button 
                      className="flex-1"
                      onClick={() => {
                        handleReceiveGoods(selectedPO);
                        setShowDetailDrawer(false);
                      }}
                    >
                      <Truck className="h-4 w-4 mr-2" />
                      Receive Goods
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    className={selectedPO.status === "fully-received" || selectedPO.status === "cancelled" ? "flex-1" : ""}
                    onClick={() => toast.info("Print functionality coming soon")}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print PO
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => toast.info("View in Access Financials")}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View in ERP
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Quick Add Purchase Order Drawer */}
      <Sheet open={showQuickAddDrawer} onOpenChange={setShowQuickAddDrawer}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center gap-2">
              <SheetTitle>Quick Add Purchase Order</SheetTitle>
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                <Zap className="h-3 w-3 mr-1" />
                Future Implementation
              </Badge>
            </div>
            <SheetDescription>
              This will be an integrated micro frontend for quickly creating purchase orders
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Vision Card */}
            <Card className="border-purple-200 bg-purple-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-purple-600" />
                  Micro Frontend Integration
                </CardTitle>
                <CardDescription>
                  This feature will be powered by a dedicated micro frontend application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <FileText className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Standalone Application</div>
                      <div className="text-sm text-muted-foreground">
                        Independent micro frontend that can be developed and deployed separately
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <Zap className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Quick Order Creation</div>
                      <div className="text-sm text-muted-foreground">
                        Streamlined form for rapid purchase order entry with smart defaults and supplier lookup
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <RefreshCw className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Real-time Sync</div>
                      <div className="text-sm text-muted-foreground">
                        Automatically syncs with Access Financials and updates the main purchase order list
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Validation & Budget Check</div>
                      <div className="text-sm text-muted-foreground">
                        Built-in validation for pricing, supplier terms, and budget approval workflows
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview of Future Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Form Preview</CardTitle>
                <CardDescription>
                  A glimpse of the quick add interface (non-functional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 opacity-60 pointer-events-none">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Supplier *</Label>
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Select supplier" />
                      </SelectTrigger>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Order Date *</Label>
                    <Input type="date" disabled />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Expected Delivery Date *</Label>
                    <Input type="date" disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Project (Optional)</Label>
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Order Lines</Label>
                  <div className="border rounded-lg p-4 space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-sm font-medium">
                      <div className="col-span-5">Stock Item</div>
                      <div className="col-span-2">Quantity</div>
                      <div className="col-span-2">Unit Price</div>
                      <div className="col-span-3">Line Total</div>
                    </div>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-5">
                        <Select disabled>
                          <SelectTrigger>
                            <SelectValue placeholder="Select item" />
                          </SelectTrigger>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input type="number" placeholder="0" disabled />
                      </div>
                      <div className="col-span-2">
                        <Input type="number" placeholder="0.00" disabled />
                      </div>
                      <div className="col-span-3">
                        <Input placeholder="0.00" disabled />
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" disabled>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Line
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>Delivery Address</Label>
                  <Input placeholder="Street address" disabled className="mb-2" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="City" disabled />
                    <Input placeholder="Postcode" disabled />
                    <Input placeholder="Country" disabled />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input placeholder="Order notes or special instructions" disabled />
                </div>
              </CardContent>
            </Card>

            {/* Technical Details */}
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  Technical Architecture
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Module Federation:</span>
                    <span className="text-muted-foreground">Webpack 5 Module Federation for runtime integration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Independent Deployment:</span>
                    <span className="text-muted-foreground">Can be updated without redeploying main app</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Shared Context:</span>
                    <span className="text-muted-foreground">Access to authentication and integration status</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Event Communication:</span>
                    <span className="text-muted-foreground">Publishes events when orders are created</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <div className="bg-muted p-4 rounded-lg text-center">
              <p className="text-sm text-muted-foreground mb-3">
                This feature is part of the roadmap for enhanced purchase order management
              </p>
              <Button variant="outline" disabled>
                <FileText className="h-4 w-4 mr-2" />
                Coming Soon
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
      />

      {/* Advanced Filters Modal */}
      <AdvancedFiltersModal
        open={showAdvancedFilters}
        onOpenChange={setShowAdvancedFilters}
        onApplyFilters={handleApplyAdvancedFilters}
        initialFilters={filterRows}
        initialLogic={filterLogic}
        entityType="purchaseOrders"
      />

      {/* Configure Columns Modal */}
      <ConfigureColumnsModal
        open={showConfigureColumns}
        onOpenChange={setShowConfigureColumns}
        columns={ALL_PO_COLUMNS}
        currentConfig={columnConfig}
        onSave={handleSaveColumnConfig}
        defaultConfig={{
          visibleColumns: DEFAULT_VISIBLE_PO_COLUMNS,
          columnOrder: ALL_PO_COLUMNS.map(col => col.id),
        }}
      />
    </div>
  );
}
